/**
 * The labelled button for the whole app.
 *
 * Before this there was no such component, and the consequences were measurable: 501 `<button>`
 * elements across 103 files, the same secondary class string copy-pasted into 29 of them, primary
 * buttons in three text sizes and four radii, and two different conventions for what hover does.
 * None of it was wrong — every one used the right tokens — but each new button was a fresh
 * decision, so the decisions drifted.
 *
 * Sizes are drawn from what the app already had rather than invented, so adopting this moves as
 * little as possible: `sm` is the compact control the task editor is built from (`text-xs`), `md`
 * the standard row button, `lg` the page-level commit.
 *
 * What this is NOT:
 *
 *   - `IconButton` — an icon alone. It owns the armed delete on a ROW: delete this block, remove
 *     this email. Same two-click grammar as `armed` here, same warning tint, different shape.
 *   - `ChoiceTile` — a stacked icon-over-label target that IS the content of its region, such as
 *     the block rail or an empty state. See that file for where the line falls.
 *
 * There is still no `danger` variant. Agavi's error token is magenta, not red, and a filled red
 * confirm is not a pattern this app has. `warning` is a different claim: not "this is destructive"
 * but "this one reaches further than the control you are standing on" — a bulk action with a
 * splash radius, like stamping the scoring default across every task on the spine or zeroing every
 * pathway at once. Those two were the first buttons to earn it; before them the variant had no
 * caller and was deliberately left unbuilt.
 */
import { forwardRef } from 'react';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'subtle' | 'warning';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'square';

/**
 * Shared by every variant, and not overridable — this is the part that drifted.
 *
 * `duration-300` matches the app's existing transition; the focus ring is the `[outline:2px_auto]`
 * form used in 43 other places rather than a ring utility, so focus looks the same here as it does
 * on everything around it.
 */
const BASE =
  'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors duration-300 '
  + 'focus-visible:[outline:2px_auto] [&:focus:not(:focus-visible)]:outline-none '
  + 'disabled:cursor-not-allowed disabled:opacity-50';

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-5 py-2.5 text-sm',
  /*
   * A FIXED SQUARE, matching the height of a field it sits beside.
   *
   * Not a convenience: a caller cannot build this by passing `w-11 px-0` in `className`,
   * because `px-0` and this table's `px-4` are the same utility at the same specificity and
   * Tailwind — not the order of the class attribute — decides which wins. `px-4` does, so the
   * caller got a 44px box with 32px of padding and about 10px left for its content. The
   * symmetry toggle on four blocks wrapped its `-/+` glyph onto three lines that way, while
   * the one block using a hand-rolled button looked right, which is what made it read as a
   * width bug rather than a padding one.
   *
   * `whitespace-nowrap` because the content is a glyph, not a label: if it ever does run out
   * of room it should overflow visibly rather than reflow into something unreadable.
   */
  square: 'h-[2.125rem] w-11 shrink-0 px-0 py-0 text-sm whitespace-nowrap',
};

/**
 * Primary darkens with its own token rather than `hover:opacity-90`, which was the commoner of the
 * two conventions but dims the LABEL along with the background — the reason the aqua buttons read
 * as washed out on hover instead of pressed.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'border border-transparent bg-ws-primary text-ws-btn-on-primary hover:bg-ws-primary-hover',
  secondary: 'border border-ws-border bg-ws-btn text-ws-text hover:bg-ws-hover',
  subtle: 'border border-transparent bg-transparent text-ws-text hover:bg-ws-hover',
  // Outlined rather than filled: at rest it has to read as available, not as an alarm. The fill
  // arrives only once it is armed, which is the moment the caution actually applies.
  warning: 'border border-ws-warning-fg/60 bg-transparent text-ws-warning-fg hover:bg-ws-warning-bg/60',
};

/**
 * The second click's worth of a two-click action, and the same tint whatever variant it came from.
 *
 * Arming is one idea, so it looks like one thing: Apply to Main Flow is `primary` at rest and
 * Clear Pathway Scores is `warning`, and both land here once armed. That is deliberate — the armed
 * state is a question ("really?"), and a question should not change appearance based on what was
 * being asked.
 *
 * It matches `IconButton`'s armed delete tint, so a teacher who has learned what the amber confirm
 * means on a row's trash icon already knows what it means here.
 */
const ARMED = 'border border-ws-warning-fg bg-ws-warning-bg text-ws-warning-fg';

/**
 * A button that is ON or OFF, rather than one that does something once.
 *
 * The Variables and Flow buttons in the task bar are these: tinted while available, filled while
 * their panel is open. They had been hand-rolled with their own `rounded-xl`, which is how they
 * came to sit beside a `rounded-md` Preview looking taller and rounder than everything else.
 *
 * Passing `pressed` also sets `aria-pressed`, which those call sites were setting by hand and
 * which a future one would have forgotten.
 */
const TOGGLE = {
  on: 'border border-ws-primary bg-ws-btn-active text-ws-btn-active-fg',
  off: 'border border-ws-primary/40 bg-ws-primary/[.08] text-ws-primary hover:bg-ws-primary/[.14] '
     + 'dark:border-ws-text-link/40 dark:bg-ws-text-link/[.12] dark:text-ws-text-link dark:hover:bg-ws-text-link/[.18]',
} as const;

/** Icons scale with the label, so a `sm` button does not carry a `md` button's icon. */
const ICON_SIZE: Record<ButtonSize, string> = {
  sm: '[&_svg]:h-3.5 [&_svg]:w-3.5',
  md: '[&_svg]:h-4 [&_svg]:w-4',
  lg: '[&_svg]:h-4 [&_svg]:w-4',
  square: '[&_svg]:h-4 [&_svg]:w-4',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. Sized and spaced here — a caller that passes its own dimensions is fighting the scale. */
  icon?: ReactNode;
  /** Trailing icon, for a chevron or an external-link mark. */
  iconTrailing?: ReactNode;
  /**
   * Working. Disables the button and marks it for screen readers, without emptying it — a button
   * that loses its label mid-action is a button the teacher cannot find again.
   */
  busy?: boolean;
  /** Fill the width of its container. */
  block?: boolean;
  /**
   * Makes this a toggle: `true` while whatever it controls is showing. Sets `aria-pressed` too, and
   * overrides `variant`, which has nothing to say about an on/off control.
   */
  pressed?: boolean;
  /**
   * Waiting for the confirming second click. Overrides `variant` — see `ARMED`.
   *
   * The caller owns the label and must swap it to the confirm wording, because that is what makes
   * the change audible: the accessible name changes under a focused button. A colour change alone
   * is not an announcement. `IconButton`'s callers do the same thing with `aria-label`.
   *
   * The caller also owns disarming — on blur, and on pointer-leave for a mouse. A button left
   * armed because the teacher looked away is a button that fires on a click meant for something
   * else.
   */
  armed?: boolean;
  /**
   * `submit` only where a form genuinely posts. The default is `button`, because a bare `<button>`
   * inside a form submits it, and most of these sit inside one incidentally.
   */
  type?: 'button' | 'submit';
  /**
   * Render as a `span` that merely LOOKS like this button, for a place where a real one is invalid.
   *
   * The sensor blocks' unselected card mounts its whole student surface inside the card's own
   * activation button, and a nested `<button>` is invalid HTML that browsers may reparent. All
   * three of them had forked this component's class string to get an inert twin, which is how
   * their capture buttons came to be `rounded-lg` beside a `rounded-md` app. Taking the look from
   * here instead is the point of the component.
   *
   * Inert by construction: no click handler is attached and no `disabled` attribute is emitted,
   * because a span has neither. Pair it with `disabled` for the dimmed treatment.
   */
  as?: 'button' | 'span';
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, iconTrailing, busy = false, block = false,
    pressed, armed = false, type = 'button', as = 'button', disabled, className = '', children, ...props },
  ref,
) {
  // Armed first: it is a state the button is IN, and it outranks both what the button normally
  // looks like and whatever it is toggling.
  const look = armed ? ARMED
    : pressed === undefined ? VARIANT[variant]
    : pressed ? TOGGLE.on : TOGGLE.off;
  const shape = `${BASE} ${SIZE[size]} ${ICON_SIZE[size]} ${look} ${block ? 'w-full' : ''} ${className}`;
  // A span carries the look and nothing else: no `type`, no `disabled`, no handlers. Spreading
  // `props` would put an `onClick` on an element that cannot be reached by keyboard, which is a
  // worse bug than the duplication this replaces.
  if (as === 'span') {
    return (
      <span className={`${shape} ${disabled ? 'opacity-50' : ''}`}>
        {icon}
        {children}
        {iconTrailing}
      </span>
    );
  }
  return (
    <button
      {...props}
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      /**
       * Suppressed for a DISCLOSURE, which carries its state in `aria-expanded` already.
       *
       * The sensor panel's pill is toggle-shaped and opens a panel, so it wants `pressed` for the
       * look and `aria-expanded` for the meaning. Announcing both would say the same fact twice in
       * two vocabularies — "button, pressed, expanded" — where the second is the one that tells a
       * listener what pressing it will do.
       *
       * FALLS BACK TO THE CALLER'S OWN `aria-pressed`, which this used to erase. `pressed` does
       * two jobs — the toggle LOOK and the announcement — and a caller that wants only the second
       * had nowhere to put it: spreading `aria-pressed` got silently overwritten with `undefined`
       * one line later, so the attribute vanished. The tolerance symmetry toggles lost their
       * state that way the day they moved onto this component, announcing as plain buttons.
       */
      aria-pressed={props['aria-expanded'] !== undefined ? undefined : (pressed ?? props['aria-pressed'])}
      data-armed={armed || undefined}
      className={shape}
    >
      {busy ? <Spinner /> : icon}
      {children}
      {iconTrailing}
    </button>
  );
});

/** Inline rather than an icon-library import, so the primitive has no dependency of its own. */
function Spinner() {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'
      className='animate-spin motion-reduce:animate-none' aria-hidden='true'>
      <path d='M21 12a9 9 0 1 1-6.2-8.6' />
    </svg>
  );
}
